// The stearns account order pages' rich order block: the collapsible
// .order-summary-details panel plus the grouped, bundle-suppressed line
// sections with named-status progress bars and tracking links. Parses the
// shared documents' opaque account_rich_order JSON seam
// (StearnsAccountRichOrderInterface -- the shared serialize_rich_order_detail
// data) and renders the legacy order-partial markup
// byte-exact. The shared RichOrderDetail component is NOT used here on
// purpose: it emits sealy's aria-accordion markup, whereas stearns keeps the
// legacy .accordion / .accordion-target class contract that _accordion.scss
// styles (.accordion-target--is-active drives the panel open). The collapsible
// summary panel is owned by local disclosure state on the order-details button
// (see OrderSummaryDetails).
//
// Returns null on an empty or unparseable payload so a parse failure degrades
// only this block, never the whole document.
import { Immutable } from "immer";
import React from "react";
import { OrderCancelButton } from "tsi-common-react/src/apps/customer-account/OrderCancelButton/index";

import { interfaces } from "@reactivated";

type IOrder = interfaces.StearnsAccountRichOrderInterface["order"];
type ISummary = IOrder["summary"];
type ILine = IOrder["shipped_events"][number]["lines"][number];

const REGISTER_PRODUCT_URL = "https://register.stearnsandfoster.com/";

// A titled progress bar with an optional guide item. An
// empty title renders an empty div.progress-bar (no matching branch); an empty
// modifier renders only the title (no guide).
const ProgressBar = (
    props: Immutable<{ bar: ILine["progress_bar"] }>,
): React.JSX.Element => {
    const { bar } = props;
    return (
        <div className="progress-bar">
            {bar.title && (
                <div className="progress-bar__list">
                    <h3 className="progress-bar__title">{bar.title}</h3>
                    {bar.item_modifier && (
                        <div className="progress-bar__guide">
                            <div
                                className={`progress-bar__item progress-bar__item--${bar.item_modifier}`}
                            ></div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const LinePrice = (
    props: Immutable<{ price: NonNullable<ILine["price"]> }>,
): React.JSX.Element => {
    const { price } = props;
    return (
        <div className="product__price-container">
            {price.is_discounted ? (
                <>
                    <span className="product__actual-price">
                        {price.actual_display}
                    </span>
                    {price.addon_display && (
                        <>
                            {" "}
                            <span className="product__retail-price">
                                +{price.addon_display}
                            </span>
                        </>
                    )}{" "}
                    <span className="product__retail-price product__retail-price--strike">
                        {price.msrp_display}
                    </span>
                </>
            ) : (
                <>
                    <span className="product__retail-price">
                        {price.actual_display}
                    </span>
                    {price.addon_display && (
                        <>
                            {" "}
                            <span className="product__retail-price">
                                +{price.addon_display}
                            </span>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

// A single order line: image, title, qty, price, reviews, progress bar,
// and tracking link.
const OrderLine = (props: Immutable<{ line: ILine }>): React.JSX.Element => {
    const { line } = props;
    return (
        <div className="order-detail">
            <div className="section__left products__results">
                <div className="product__image">
                    <a href={line.pdp_url}>
                        {line.image_url && (
                            <img
                                className="responsive-img"
                                src={line.image_url}
                                alt={line.image_alt}
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </a>
                </div>
                <div className="product__details">
                    <div className="product__details__copy">
                        {line.title}
                        <br />
                        Qty: {line.quantity}
                        <br />
                        {!line.is_bundled && line.price && (
                            <LinePrice price={line.price} />
                        )}
                        {line.is_bundled && (
                            <div className="order-detail__bundle-callout">
                                {line.bundle_callout_text}
                            </div>
                        )}
                        <div className="product__reviews">
                            {line.reviews.has_write_review && (
                                <a
                                    href={line.reviews.write_review_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Write a Review
                                </a>
                            )}
                            {line.reviews.show_divider && (
                                <>
                                    {" "}
                                    <span className="product__copy-divider">
                                        &#124;
                                    </span>{" "}
                                </>
                            )}
                            {line.reviews.show_register_link && (
                                <a
                                    href={REGISTER_PRODUCT_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Register Product
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="section__right product__status">
                <ProgressBar bar={line.progress_bar} />
                {line.show_submit_failed_copy && (
                    <div className="product__status__copy">
                        <p className="product__shipping-status">
                            Online tracking is unavailable for this order.
                            Please contact us at 800-821-6621 if you have
                            questions about your order.
                        </p>
                    </div>
                )}
                <div className="product__status__copy">
                    {line.tracking.show_link && (
                        <div className="product__shipping-status--tracking-link-container">
                            <a
                                className={
                                    line.tracking.is_freight
                                        ? "button product__shipping-status--wg-tracking product__shipping-status--tracking-link al-shipping--wg-tracking"
                                        : "button product__shipping-status--tracking-link"
                                }
                                href={line.tracking.tracking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Track Shipment
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// The collapsible summary panel, owning its open/closed disclosure state. The
// order-details button toggles it; the state drives `accordion-target--is-active`
// on the `.accordion-target` panel (the modifier _accordion.scss styles open)
// and the `--close` arrow-flip on the button. The header keeps the legacy
// `.accordion` class (styled `cursor: pointer`) and its inert `accordion--is-active`
// modifier tracks the same state, matching the legacy toggle. The button carries
// no aria (legacy parity -- the legacy markup had none). Closed by default.
const OrderSummaryDetails = (
    props: Immutable<{ summary: ISummary }>,
): React.JSX.Element => {
    const { summary } = props;
    const addr = summary.shipping_address;
    const [isOpen, setIsOpen] = React.useState(false);
    return (
        <div className="order-summary-details details">
            <div
                className={`order-summary-details__header accordion${
                    isOpen ? " accordion--is-active" : ""
                }`}
            >
                <div className="order-summary-details__info">
                    <h3 className="order-summary-details__order-date">
                        {summary.date_placed_display}
                    </h3>
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-number">
                        Order Number: {summary.order_number}
                    </div>
                    <div className="order-summary-details__info-column order-summary-details__info-column--total-price-wrapper">
                        Total:{" "}
                        <span className="order-summary-details__info-column--total-price">
                            {summary.order_total_display}
                        </span>
                    </div>
                </div>
                <div className="order-summary-details__info">
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-actions">
                        {summary.cancel_button && (
                            <OrderCancelButton {...summary.cancel_button} />
                        )}
                    </div>
                </div>
                <button
                    className={`order-summary-details__info-column order-summary-details__info-column--order-details${
                        isOpen
                            ? " order-summary-details__info-column--order-details--close"
                            : ""
                    }`}
                    onClick={(event) => {
                        event.preventDefault();
                        setIsOpen((open) => !open);
                    }}
                >
                    <span className="order-summary-details__order-details">
                        Order Details
                    </span>
                </button>
            </div>
            <div
                className={`accordion-target${
                    isOpen ? " accordion-target--is-active" : ""
                }`}
            >
                <div className="order-summary-details__columns">
                    <div className="order-summary-details__column">
                        <h4 className="order-summary-details__column__title">
                            Shipping Address
                        </h4>
                        <div className="order-summary-details__copy-block order-summary-details__address">
                            <div>{addr.name}</div>
                            <div>
                                {addr.line1}
                                {addr.line2 && <>, {addr.line2}</>}
                                {addr.line3 && <>, {addr.line3}</>}
                            </div>
                            <div>
                                {addr.city}, {addr.state} {addr.postcode}
                                <br />
                                {addr.phone_number}
                            </div>
                        </div>
                    </div>

                    <div className="order-summary-details__column">
                        <h4 className="order-summary-details__column__title">
                            Payment Method
                        </h4>
                        <div className="order-summary-details__copy-block order-summary-details__payment">
                            {summary.cybersource_cards.map((card, i) => (
                                <div
                                    key={`cs-${i}`}
                                    className="order-summary-details__cc_payment"
                                >
                                    <span className="order-summary-details__card_type">
                                        {card.card_type_name}
                                    </span>{" "}
                                    <span className="order-summary-details__card_mask">
                                        ending in {card.last4}
                                    </span>
                                    {card.amount_display && (
                                        <span className="order-summary-details__amount">
                                            {card.amount_display}
                                        </span>
                                    )}
                                </div>
                            ))}
                            {summary.financing_transfers.map((transfer, i) => (
                                <div
                                    key={`fin-${i}`}
                                    className="order-summary-details__financing_payment"
                                >
                                    <div className="order-summary-details__account">
                                        <span>
                                            Account ending in{" "}
                                            {transfer.masked_account_number}
                                        </span>
                                        {transfer.amount_display && (
                                            <span className="order-summary-details__amount">
                                                {transfer.amount_display}
                                            </span>
                                        )}
                                    </div>
                                    {transfer.disclosure && (
                                        <span className="order-summary-details__terms">
                                            Terms: {transfer.disclosure}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="order-summary-details__breakdown order-summary-details__column">
                        <h4 className="order-summary-details__column__title">
                            Order Summary
                        </h4>
                        <div className="order-summary-details__copy-block">
                            <div className="order-summary-details__breakdown-row">
                                <div className="order-summary-details__breakdown-title">
                                    Subtotal:
                                </div>
                                <div className="order-summary-details__breakdown-number">
                                    {summary.subtotal_display}
                                </div>
                            </div>

                            <div className="order-summary-details__breakdown-row">
                                <div className="order-summary-details__breakdown-title">
                                    Shipping:
                                </div>
                                <div className="order-summary-details__breakdown-number">
                                    {summary.shipping_display}
                                </div>
                            </div>

                            {summary.is_tax_known ? (
                                summary.itemized_tax_groups.map((group, i) => (
                                    <div
                                        key={i}
                                        className="order-summary-details__breakdown-row"
                                    >
                                        <div className="order-summary-details__breakdown-title">
                                            {group.name}
                                        </div>
                                        <div className="order-summary-details__breakdown-number">
                                            {group.amount_display}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="order-summary-details__breakdown-row">
                                    <div className="order-summary-details__breakdown-title">
                                        Tax:
                                    </div>
                                    <div className="order-summary-details__breakdown-number">
                                        Tax will be calculated upon shipment
                                    </div>
                                </div>
                            )}

                            <div className="order-summary-details__breakdown-row">
                                {summary.has_discount && (
                                    <>
                                        <div className="order-summary-details__breakdown-title">
                                            Discount:
                                        </div>
                                        <div className="order-summary-details__breakdown-number order-summary-details__breakdown-number--cerise">
                                            {summary.discount_display}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="order-summary-details__breakdown-row order-summary-details__breakdown-row--total">
                                <div className="order-summary-details__breakdown-title">
                                    Order Total:
                                </div>
                                <div className="order-summary-details__breakdown-number">
                                    {summary.order_total_display}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// The summary panel plus the shipped-event and
// unshipped line sections. Typed against the shared rich-order serialization:
// the account order pages parse it from the opaque account_rich_order JSON
// seam (AccountRichOrder below), while the anonymous order-status document
// feeds the same three fields flattened on its page props. A null summary
// renders no block (the anon page's no-order empty states carry none).
export const AccountRichOrderBody = (
    props: Immutable<{
        summary: ISummary | null;
        shipped_events: IOrder["shipped_events"];
        unshipped: IOrder["unshipped"];
    }>,
): React.JSX.Element | null => {
    const { summary, shipped_events, unshipped } = props;
    if (!summary) {
        return null;
    }
    return (
        <div className="order-summary">
            <OrderSummaryDetails summary={summary} />

            {shipped_events.map((event, i) => (
                <div
                    key={i}
                    className="order-summary__body order-summary__body--shipping-events-"
                >
                    <div className="section products">
                        {event.lines.map((line, j) => (
                            <OrderLine key={j} line={line} />
                        ))}
                    </div>
                </div>
            ))}

            {unshipped && (
                <div className="order-summary__body order-summary__body--unshipped-order">
                    <div className="section products">
                        {unshipped.lines.map((line, i) => (
                            <OrderLine key={i} line={line} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// The account order pages' seam: parses the opaque account_rich_order JSON
// string (StearnsAccountRichOrderInterface) and renders the order block.
// Returns null on an empty or unparseable payload so a parse failure degrades
// only this block, never the whole document.
export const AccountRichOrder = ({
    payload,
}: {
    payload: string;
}): React.JSX.Element | null => {
    if (!payload) {
        return null;
    }
    let order: IOrder | undefined;
    try {
        const parsed = JSON.parse(
            payload,
        ) as interfaces.StearnsAccountRichOrderInterface | null;
        order = parsed?.order;
    } catch {
        return null;
    }
    // Valid-JSON-but-wrong-shape payloads (e.g. "{}" or "null") parse without
    // throwing but leave `order` undefined; guard so those degrade to null here
    // rather than crashing the whole document on `order.summary` below.
    if (!order) {
        return null;
    }
    return (
        <AccountRichOrderBody
            summary={order.summary}
            shipped_events={order.shipped_events}
            unshipped={order.unshipped}
        />
    );
};
